import signUpClient from '@/core/apis/signUp';
import {
    arrayBufferFromBase64,
    arrayBufferToBase64,
    decryptSignupData,
    encryptSignupData,
} from '@/modules/Signup/services/signupCrypto';
import { SignupConstants } from '@/service/Constants';
import ls from '@/stores/local-storage';
import { generatePassword } from '@/utilities/securityUtils';

export async function signUpLogin() {
    try {
        const res = await signUpClient.post('/sign-up-login', {});
        const token = res.detail.accessToken;
        signUpClient.token = token;

        return {
            success: true,
            token,
            tokenObj: res.detail.token,
            parentAccountId: res.parent_account_id,
        };
    } catch (error) {
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

/**
 * @typedef {Object}    LoadRequestPayload
 * @property {number}   country_id
 * @property {string}   address
 *
 * @param {LoadRequestPayload}  requestPayload
 * @param {AbortSignal}         signal
 * @returns {Promise<*>}
 */
export async function loadAddress(requestPayload, signal) {
    return await signUpClient.post('/search-address', requestPayload, { signal });
}

/**
 * @typedef {Object}    LoadRequestPayload
 * @property {string}   PlaceID
 * @property {string}   FormattedAddress
 * @property {object}   MapAddress
 * @property {number}   ServiceTypeMapID
 *
 * @param {LoadRequestPayload}  payload
 * @returns {Promise<*>}
 */
export async function processFindNBNLocation(payload) {
    const requestPayload = {
        ...payload,
        ServiceTypeMapID: 9009,
    };

    try {
        const res = await signUpClient.post('/find-nbn-location', requestPayload);
        const nbnLocations = res.NBNLocation || {};

        if (
            nbnLocations &&
            Object.keys(nbnLocations).length > 0 &&
            !Object.prototype.hasOwnProperty.call(nbnLocations, 'error')
        ) {
            return {
                success: true,
                nbnLocations,
                addressId: res.AddressID,
            };
        }

        return {
            success: false,
            message: 'Request failed.',
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: 'An unexpected error occurred while finding NBN location.',
        };
    }
}

/**
 * @typedef {Object}    LoadRequestPayload
 * @property {string}   DirectoryID
 * @property {number}   ServiceTypeMapID
 *
 * @param {LoadRequestPayload}  payload
 * @returns {Promise<*>}
 */
export async function processPerformSimpleNbnSq(payload) {
    const requestPayload = {
        ServiceTypeMapID: 10281,    // default value
        DirectoryID: payload.DirectoryID,
    };

    try {
        const res = await signUpClient.post('/perform-simple-nbn-sq', requestPayload);
        const serviceData = res.ServiceData || {};

        const
            orderingOptions = serviceData.OrderingOptions,
            failureReasons = serviceData.QualificationResponses?.[0]?.FailureReasons,
            sqResponse = serviceData.Raw_SQ_Response;

        if (sqResponse.Result === 'PASS' && orderingOptions && Object.keys(orderingOptions).length > 0) {
            return {
                success: true,
                orderingOptions,
                sqResponse,
            };
        }

        return {
            success: false,
            message: failureReasons,
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: 'An unexpected error occurred while checking service qualification.',
        };
    }
}

/**
 * @typedef {Object}    LoadRequestPayload
 * @property {number}   ParentAccountID
 * @property {array}    ServiceTypes
 * @property {object}   SQ_Result
 *
 * @param {LoadRequestPayload}  payload
 * @returns {Promise<void>}
 */
export async function getAvailablePackages(payload) {
    const requestPayload = {
        ServiceTypes: [{ id: 5389 }],   // default service type
        ParentAccountID: payload.ParentAccountID,
        SQ_Result: payload.SQ_Result,
    };

    try {
        const resPkgPlans = await signUpClient.post('/get-available-packages', requestPayload);
        const packagePlans = resPkgPlans.PackagePlans || [];

        if (packagePlans.length > 0 && !resPkgPlans.error) {
            const resPkgDtls = await signUpClient.post('/get-package-plan-details', {
                'PackagePlanIDs': packagePlans.map(plan => plan.id), // Extracted package plan ids
                'AccountID': payload.ParentAccountID,   // assumes that this is the PackagePlans[].account_id
            });

            const packagePlansDTO = resPkgDtls.PackagePlanDetails.map(res => {
                const servicePlans = res?.ServicePlans?.ServicePlan ?? [];

                // Get the package plan amount
                const amount = servicePlans.reduce((sum, plan) => {
                    return sum + parseFloat(plan.AccessFee?.Amount || 0);
                }, 0);

                // Mapped properties
                const properties = servicePlans.map(sp => {
                    const [download, upload] = sp.Properties.find(r => r.key === 'VOCUS_SERVICE_SPEED')
                        .value
                        .replaceAll('Mbps', '')
                        .split('/');

                    const technologies = Object.values(sp.Properties.find(r => r.key === 'TECHNOLOGY').value)

                    return {
                        speed: { download, upload },
                        technologies,
                    };
                })[0];

                // eslint-disable-next-line no-unused-vars
                const { ServicePlans, ...rest } = res;

                return {
                    ...rest,
                    properties,
                    amount,     // attach computed total
                };
            });

            return {
                success: true,
                packagePlans: packagePlansDTO,
            };
        }

        return {
            success: false,
            message: 'No Plans Available.',
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: 'An unexpected error occurred while checking available packages.',
        };
    }
}

/**
 * Encrypts account data and stores it in localStorage
 *
 * @param {Object} accountData  The account data to encrypt
 * @returns {Promise<boolean>}
 */
export async function encryptAndStoreAccountData(accountData) {
    try {
        const { encryptedData, iv } = await encryptSignupData(
            JSON.stringify(accountData),
            process.env.VUE_APP_SIGNUP_ENCRYPTION_KEY || 'nimbus-sign-up-encryption-key',
        );

        // Convert to base64 strings
        const encryptedDataBase64 = arrayBufferToBase64(encryptedData);
        const ivBase64 = arrayBufferToBase64(iv);

        // Store to localstorage
        try {
            ls.set(SignupConstants.ACCOUNT_DATA_KEY, encryptedDataBase64);
            ls.set(SignupConstants.ACCOUNT_DATA_CRYPT_IV, ivBase64);

            // Verify data was stored correctly
            const storedData = ls.get(SignupConstants.ACCOUNT_DATA_KEY);
            const storedIv = ls.get(SignupConstants.ACCOUNT_DATA_CRYPT_IV);

            if (!storedData || !storedIv) {
                console.error('Failed to store data using vue-ls');
                return false;
            }

            return true;
        } catch (storageError) {
            console.error('Error storing data:', storageError);
            return false;
        }
    } catch (error) {
        console.error('Error encrypting account data:', error);
        return false;
    }
}

/**
 * Retrieves and decrypts account data from localStorage
 *
 * @returns {Promise<Object|null>}  The decrypted account data or null if an error occurred
 */
export async function retrieveAndDecryptAccountData() {
    try {
        const encryptedDataString = ls.get(SignupConstants.ACCOUNT_DATA_KEY);
        const ivString = ls.get(SignupConstants.ACCOUNT_DATA_CRYPT_IV);

        if (!encryptedDataString || !ivString) {
            console.error('Data not found.');

            // Fallback to native localStorage as a backup
            const nativeEncryptedData = localStorage.getItem(SignupConstants.ACCOUNT_DATA_KEY);
            const nativeIv = localStorage.getItem(SignupConstants.ACCOUNT_DATA_CRYPT_IV);

            if (!nativeEncryptedData || !nativeIv) {
                console.error('Data not found in localStorage');
                return null;
            }

            // If found in localStorage
            ls.set(SignupConstants.ACCOUNT_DATA_KEY, nativeEncryptedData);
            ls.set(SignupConstants.ACCOUNT_DATA_CRYPT_IV, nativeIv);

            // Continue with the native data
            const encryptedData = arrayBufferFromBase64(nativeEncryptedData);
            const iv = arrayBufferFromBase64(nativeIv);

            if (!encryptedData || !iv) {
                console.error('Failed to convert native data from base64');
                return null;
            }

            const decryptedData = await decryptSignupData(
                encryptedData,
                iv,
                process.env.VUE_APP_SIGNUP_ENCRYPTION_KEY || 'nimbus-sign-up-encryption-key',
            );

            return JSON.parse(decryptedData);
        }

        // Convert from base64 strings to ArrayBuffer
        const encryptedData = arrayBufferFromBase64(encryptedDataString);
        const iv = arrayBufferFromBase64(ivString);

        if (!encryptedData || !iv) {
            console.error('Failed to convert data from base64');
            return null;
        }

        // Decrypt the data
        const decryptedData = await decryptSignupData(
            encryptedData,
            iv,
            process.env.VUE_APP_SIGNUP_ENCRYPTION_KEY || 'nimbus-sign-up-encryption-key',
        );

        // Parse the JSON data
        return JSON.parse(decryptedData);
    } catch (error) {
        console.error('Error retrieving and decrypting account data:', error);
        return null;
    }
}

/**
 * Clears the encrypted account data from localStorage
 *
 * @returns {boolean}
 */
export function clearEncryptedAccountData() {
    try {
        // Remove items
        ls.remove(SignupConstants.ACCOUNT_DATA_KEY);
        ls.remove(SignupConstants.ACCOUNT_DATA_CRYPT_IV);

        // Clear from localStorage as well
        try {
            localStorage.removeItem(SignupConstants.ACCOUNT_DATA_KEY);
            localStorage.removeItem(SignupConstants.ACCOUNT_DATA_CRYPT_IV);
        } catch (nativeError) {
            console.warn('Error clearing localStorage:', nativeError);
        }

        // Verify items were removed from ls
        const dataStillExists = ls.get(SignupConstants.ACCOUNT_DATA_KEY) !== null;
        const ivStillExists = ls.get(SignupConstants.ACCOUNT_DATA_CRYPT_IV) !== null;

        if (dataStillExists || ivStillExists) {
            console.error('Failed to clear data');
            return false;
        }

        console.log('Data cleared successfully');
        return true;
    } catch (error) {
        console.error('Error clearing data:', error);
        return false;
    }
}

/**
 * User must create an account
 *
 * @param payload
 * @returns {Promise<void>}
 */
export async function accountCreation(payload) {
    const {
        firstname,
        lastname,
        email,
        phone,
        dob,
        billingAddress,
        // shippingAddress,
        // shipping_method,
        // has_existing_connection,
        userId, // Get userId from payload
    } = payload;

    if (!userId) {
        console.error('userId is not set in the payload');
        throw new Error('userId is required for account creation');
    }

    const requestPayload = {
        account: {
            type: 'individual',
            account_type: {
                id: 3,
                name: 'End User',
            },
            contact: {
                primary: true,
            },
            account_type_id: 3,
            profile: {
                id: 1108,
                name: 'EmersionTest Default Account Profile',
                description: 'EmersionTest Default Account Profile',
                default_profile: '1',
            },
            profile_id: 1108,
            account_country: {
                id: 113,
                name: 'Australia',
                iso_code: 'AU',
            },
            account_timezone_data: {
                id: 67,
                name: 'Eastern Australia Time (Melbourne)',
                description: 'A.U.S. Eastern Standard Time',
                tz_timezone: 'Australia/Melbourne',
                default: false,
            },
            account_timezone: 67,
            contacts: [
                {
                    job_title: null,
                    firstname: firstname,
                    middlename: '',
                    lastname: lastname,
                    date_of_birth: dob,
                    contact_type: 'primary',
                    created_by_id: userId,
                    modified_by_id: userId,
                    model_status: 2,
                    contacts: [
                        {
                            phone_number_type_id: 11,
                            phonenumber: phone, // This is required
                            country_id: null,
                            preferred: true,
                            created_by_id: userId,
                            modified_by_id: userId,
                            model_status: 2,
                        },
                    ],
                    email_address: {
                        email_address_type_id: 102,
                        address: email,
                        created_by_id: userId,
                        modified_by_id: userId,
                        model_status: 2,
                    },
                    internet_messaging: {
                        address: null,
                        service_provider: null,
                        created_by_id: userId,
                        modified_by_id: userId,
                        model_status: 2,
                    },
                    web_address: {
                        url: null,
                        created_by_id: userId,
                        modified_by_id: userId,
                        model_status: 2,
                    },
                    physical_address: {
                        address: billingAddress,
                        address_line_1: '',
                        address_building_name: null,
                        address_site_name: null,
                        street_number_start: '472',
                        street_number_end: null,
                        street_name: 'Esplanade',
                        suburb: 'Torquay',
                        postcode: '4655',
                        created_by_id: userId,
                        modified_by_id: userId,
                        model_status: 2,
                        address_physical_state: {
                            id: 103,
                            name: 'Queensland',
                            country_id: '113',
                        },
                        address_street_type: {
                            id: 648,
                            name: 'Anex',
                        },
                        addr_complex_unit_num: '1',
                        address_complex_unit_type: {
                            id: 142,
                            name: 'Substation',
                        },
                        addr_complex_level_num: '2',
                        address_complex_level_type: {
                            id: 106,
                            name: 'Lower Level',
                        },
                        street_lot_number: null,
                        nearest_cross_street: null,
                        address_line_2: null,
                        zip_plus_4: null,
                        address_complex_unit_type_id: 142,
                        address_complex_level_type_id: 106,
                        address_street_suffix_id: null,
                        address_physical_state_id: 103,
                        address_street_type_id: 648,
                        country: {
                            id: 113,
                            name: 'Australia',
                            iso_code: 'AU',
                        },
                        country_id: 113,
                    },
                },
            ],
            account_start_date: new Date(),
            current_stage: 3,
            created_by_id: userId,
            modified_by_id: userId,
            model_status: 2,
        },
    };

    try {
        const res = await signUpClient.post('/account/store', requestPayload);
        if (res.success) {
            return {
                success: true,
                data: {
                    customerAccountId: res.data.id,
                    contactCollections: res.data.primary_contact, // TODO: refactor - explicitly exposed only fields required.
                },
            };
        }

        return {
            success: false,
        };
    } catch (error) {
        console.error('Error in accountCreation API call:', error);
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

/**
 * @typedef {Object}            LoadRequestPayload
 * @property {string}           CustomerAccountID
 * @property {string}           ParentAccountID
 * @property {array}            ProductIDs
 * @property {string}           PackagePlanID
 * @property {string}           DirectoryID
 * @property {string}           avcId
 * @property {null|boolean}     ntdId
 * @property {string}           orderingOption
 *
 * @typedef {Object}            LoadResponsePayload
 * @property {boolean}          success
 * @property {Object}           data
 * @property {number}           data.accountPresaleSQ
 * @property {number}           data.accountPresale
 *
 * @param {LoadRequestPayload}  payload
 * @returns {Promise<LoadResponsePayload>}
 */
export async function initiateOrder(payload) {
    const {
        customerAccountId,
        parentAccountId,
        productIds,
        packagePlanId,
        directoryId,
        avcId,
        ntdId,
        orderingOption,
    } = payload;

    const requestPayload = {
        CustomerAccountID: customerAccountId,
        ParentAccountID: parentAccountId,
        AVCID: avcId,
        TransferType: {
            OrderingOption: orderingOption,
            PhoneNumber: null,
            KeepNumber: null,
            NTDID: ntdId,
        },
        ProductIDs: productIds,
        PackagePlanID: packagePlanId,
        ServiceTypeID: 5389,
        ServiceTypeMapID: 10281,
        DirectoryID: directoryId,
    };

    try {
        return await signUpClient.post('/initiate-order', requestPayload);
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

export async function setServiceConfigVocusNbn(payload) {
    const contactCollection = payload.contactCollection;
    const basicPassword = generatePassword();

    const requestPayload = {
        AccountPresaleSQID: payload.accountPresaleSq,
        AddressID: payload.addressId,
        Username: payload.username,     // account id
        Password: basicPassword,
        Realm: 'ezicom.com.au',
        ServiceTypeID: 5389,
        ContactCollections: {
            Address: {
                Type: 'StandardAddressDetails',
                TypeName: 'Site Contact',
                LotNumber: contactCollection.address.lotNumber,
                StreetNumberStart: contactCollection.address.streetNumberStart,
                StreetNumberEnd: contactCollection.address.streetNumberEnd,
                StreetName: contactCollection.address.streetName,
                StreetType: contactCollection.address.streetType,
                Suburb: contactCollection.address.suburb,
                State: contactCollection.address.state,
                Postcode: contactCollection.address.postcode,
                Country: contactCollection.address.country,
            },
            Details: {
                Type: contactCollection.details.type,
                Title: contactCollection.details.title,
                FirstName: contactCollection.details.firstName,
                LastName: contactCollection.details.lastName,
                DateOfBirth: contactCollection.details.dob,
                ContactType: contactCollection.details.contactType,
            },
            PhoneNumber: {
                Type: contactCollection.phone.type,
                Number: contactCollection.phone.number,
            },
            EmailAddress: {
                Type: contactCollection.email.type,
                Address: contactCollection.email.address,
            },
        },
    };

    try {
        const res = await signUpClient.post(
            '/set-all-service-configuration-vocus-nbn',
            requestPayload,
        );
        return {
            success: true,
            data: res.data,
        };
    } catch (error) {
        console.error(error);
        return {};
    }
}

/**
 * Fetches a list of all available banks from the server.
 */
export async function getAllBanks() {
    try {
        const res = await signUpClient.get('/bank');
        return {
            success: true,
            data: res.data,
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

// TODO: can be refactor alongside credit card.
/**
 * @param accountId
 * @param payload
 * @param isRecurring       This is a flag from the UI that determines if the payment method is set to be recurring or not.
 *
 * @returns {Promise<{success: boolean}|{success: boolean, message: *}>}
 */
export async function paymentMethodBankAccount(accountId, payload, isRecurring = true) {
    const dtoUpfront = { ...payload.upfront, is_default: 'yes' };
    const dtoRecurring = { ...payload.recurring, is_default: 'yes' };

    // Holds the response for the upfront payment which will be used by `perform-payment`
    let dtoPaymentMethodRes;
    try {
        if (isRecurring) {
            // Only recurring, since both data from upfront and recurring are the same
            dtoPaymentMethodRes = await signUpClient.post(`/payment-method/bank-account/${accountId}`, dtoRecurring);
        } else {
            // Save both upfront and recurring
            dtoPaymentMethodRes = await signUpClient.post(`/payment-method/bank-account/${accountId}`, dtoUpfront);
            await signUpClient.post(`/payment-method/bank-account/${accountId}`, dtoRecurring);
        }

        await processAutomatedPaymentInfo(accountId);

        return {
            success: true,
            data: dtoPaymentMethodRes.data,
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

// TODO: can be refactor. D.R.Y.
// Same clarifications from bank account
export async function paymentMethodCreditCardAccount(accountId, payload, isRecurring = true) {
    const dtoUpfront = {
        cardType: payload.upfront.ccCardType,
        card_number: payload.upfront.ccCardNumber,
        card_name: payload.upfront.ccCardName,
        // TODO: clarify this part since there's already an expiry_date
        // expiryMonth: payload.expiryMonth,   // ???
        // expiryYear: payload.expiryYear,     // ???
        cvv: payload.upfront.ccCvv,
        expiry_date: payload.upfront.ccExpiryDate,
        is_default: '1',
    };

    const dtoRecurring = {
        cardType: payload.recurring.ccCardType,
        card_number: payload.recurring.ccCardNumber,
        card_name: payload.recurring.ccCardName,
        cvv: payload.recurring.ccCvv,
        expiry_date: payload.recurring.ccExpiryDate,
        is_default: '1',
    };

    let dtoPaymentMethodRes;
    try {
        if (isRecurring) {
            dtoPaymentMethodRes = await signUpClient.post(`/payment-method/credit-card/${accountId}`, dtoRecurring);
        } else {
            dtoPaymentMethodRes = await signUpClient.post(`/payment-method/credit-card/${accountId}`, dtoUpfront);
            await signUpClient.post(`/payment-method/credit-card/${accountId}`, dtoRecurring);
        }

        await processAutomatedPaymentInfo(accountId);

        return {
            success: true,
            data: dtoPaymentMethodRes.data,
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

// TODO: refactor, this should probably be process alongside creating payment
// TODO: can be refactor. D.R.Y.
async function processAutomatedPaymentInfo(customerAccountId) {
    try {
        const resGetInfo = await signUpClient.get(
            `/payments-list/${customerAccountId}/automated-payment-info`,
        );

        await signUpClient.post(
            `/payment-method/automated-payment-methods/${customerAccountId}`,
            resGetInfo.data,
        );

        return {
            success: true,
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

/**
 * @typedef {Object}            LoadRequestPayload
 * @property {string}           packagePlanId
 * @property {string|number}    accountId
 *
 * @param {LoadRequestPayload}  payload
 * @returns {Promise<{success: boolean, message: *}|{success: boolean, message: string}|{success: boolean, data: (*|{})}>}
 */
export async function getTermsAndConditions(payload) {
    const requestPayload = {
        ObjectMapping: {
            PackagePlanID: parseInt(payload.packagePlanId, 10),
        },
        AccountID: payload.accountId,
    };

    try {
        const res = await signUpClient.post('/get-terms-and-conditions', requestPayload);
        const termsAndConditions = res.TermsAndConditions || {};

        if (termsAndConditions && Object.keys(termsAndConditions).length > 0) {
            return {
                success: true,
                data: termsAndConditions,
            };
        }

        return {
            success: false,
            message: 'Request failed.',
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

export async function performPayment(accountId, payload) {
    // Format sample breakdown: 1:600362
    //  1 = debit card/bank account; 10 = cc
    //  600362 = payment method id
    const formatStoredPaymentMethod = `${payload.paymentMethod}:${payload.paymentMethodId}`;

    const requestPayload = {
        stored_payment_method: formatStoredPaymentMethod,
        amount: payload.amount,                             //
        is_take_surcharges_now: false,                      // TODO: for deliberation if its going to be shown or not
        selected_payment_gateway_map: null,
        amount_to_allocate: {},
        is_apply_ppd: false,                                // means no discount
        markup: 0,
        price: payload.price,                               // selected plans and packages
    };

    try {
        const res = await signUpClient.post(
            `/take-payment/${accountId}/perform-payment`,
            requestPayload,
        );
        return {
            success: true,
            data: res.data,
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: error.response?.message,
        };
    }
}

export async function completeOrder(payload) {
    const requestPayload = {
        AccountPresaleID: payload.accountPresaleId,
    };

    try {
        await signUpClient.post('/complete-order', requestPayload);
        return {
            success: true,
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            message: error.response?.message || error.message,
        };
    }
}
